<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="v => emit('update:isVisible', v)" isWithScroll :isClosable="false" :isCloseButtonVisible="true" size="large">
    <template #content>
      <div class="plan-edit-form" style="margin-bottom: 20px">
        <template v-if="showVisitPlanFields">
          <h1 class="global-h1 plan-edit-form__title">Share your Plan with the Destination</h1>

          <!-- prettier-ignore -->
          <div class="plan-edit-form__description">
          <h5 class="global-h5">List out the {{collabInput.requestedLocationCount}}+ locations or activities you plan to feature during this visit.</h5>
          The destination will approve your visit itinerary so that everyone is on the same page.<br/>
          <p style="margin-bottom: 12px; font: 14px/18px sans-serif;">If you have questions, just ask! <CopyTextNew>collabs@shrpa.com</CopyTextNew></p>
          
          <b>How am I supposed to visit {{collabInput.requestedLocationCount}} unique locations? </b> 
          <InfoIconWithTooltip :width="420" style="position:relative;top: 0;" icon="question-simple_4" :iconSize="11">
            <p style="font: 14px/18px sans-serif;">If your visit is focused around an event or a single location that might take a whole day to explore, it doesn't make sense to go to a bunch of places!</p>

            <p style="font: 14px/18px sans-serif;"><b>Feature aspects of the event or location.</b> Talk about the food options, live music, arts, shopping, activities, etc, as individual stops.</p>

            <p style="font: 14px/18px sans-serif;">
              <span style="margin-bottom: 4px;display: inline-block;">Creator Visits that did a great job of this:</span><br/>
              <a class="global-a" href="https://shrpa.com/collabs/beaverdamwi/468491b8-f07e-4b02-ab22-b08e69f6f253" target="_blank" style="margin-bottom: 4px; display:inline-block;text-decoration: underline">
                An Unforgettable Autumn Experience at Fall Downtown Fest
              </a><br />
              <a class="global-a" href="https://shrpa.com/collabs/waterlooia/acabe21b-1e5a-49fc-8121-80550fe04b55" target="_blank" style="text-decoration: underline">
                Lost Island Family Adventure
              </a></p>
          </InfoIconWithTooltip>
        </div>
        </template>

        <LocationsList
          v-if="props.collabInput.useLocationsInVisitPlan || (!showVisitPlanFields && props.collabInput.useLocationsInAdventures)"
          :boundaryGeoJson="boundaryData?.boundaryGeoJson"
          :locationsList="collabLocationsData.locations"
          :showValidation="errorMessage != null"
          :isVisitPlanNotesVisible="showVisitPlanFields"
          v-model:visitPlanNotes="collabLocationsStore.visitPlanNotes"
          @update:locationsList="
            newLocationsList => {
              collabLocationsData.locations = newLocationsList;
            }
          "
          :boundarySelectMode="boundaryData?.boundarySelectMode"
          :withinMiles="boundaryData?.withinMiles"
          :mapCenter="boundaryData ? { latitude: boundaryData.latitude, longitude: boundaryData.longitude } : null"
          :customerId="props.communityId"
          :collabInputSummary="props.collabInput"
          :visitPlanSentDate="props.creatorInput?.visitPlanSentDate"
          :creatorId="props.creatorId"
          @update:locationsData="
            newData => {
              collabLocationsData = newData;
              emit('updateCollabLocationsData', newData);
            }
          "
          style="margin-bottom: 20px"
        />

        <div v-if="showVisitPlanFields" style="margin-bottom: 30px">
          <NoteWithIcon v-if="isPlanWasChanged && props.creatorInput.visitPlanSignOffDate" class="plan-edit-form__notify-destination-note" color="yellow" size="small">
            <template #icon><IconEmbedded name="info-simple_4" :size="18" /></template>
            <template #text>
              <div>
                <h4 class="global-h4" style="margin-bottom: 3px">If meaningful changes are made, notify the destination.</h4>
              </div>
            </template>
          </NoteWithIcon>

          <textarea
            v-if="!props.collabInput.useLocationsInVisitPlan"
            :class="{
              'global-textarea': true,
              'plan-edit-form__plan-textarea': true,
            }"
            v-model="_visitPlan"
            placeholder="1. Central Perk
2. Krusty Krab
3. Willy Wonka's Chocolate Factory
4. …
"
          ></textarea>
        </div>

        <template v-if="showVisitPlanFields">
          <div class="plan-edit-form__divider"></div>

          <!-- "Send" and "Connect" buttons -->
          <div class="send-and-connect-buttons" style="margin-bottom: 27px">
            <div v-if="errorMessage" class="validation-message">
              <b>{{ errorMessage }}</b>
            </div>
            <SrpButton @click="sendToDestination" :isDisabled="isSendingToDestination">
              <template #icon>
                <LoadingIcon v-if="isSendingToDestination" :size="22" />
                <IconEmbedded v-else name="send_2-5" style="margin-right: 6px" :size="22" color="rgba(255, 255, 255, 0.5)" />
              </template>
              Send Plan to Destination
            </SrpButton>

            <LinkWithIcon isDottedUnderline @click="switchToChat" color="blue">
              <template #icon><IconEmbedded name="chat_2-5" :size="19" /></template>
              <span>Connect with the Destination</span>
            </LinkWithIcon>
          </div>
          <!-- / "Send" and "Connect" buttons -->

          <div class="plan-edit-form__divider"></div>

          <SrpDetailsSummary class="plan-edit-form__activities-and-boundary-spoiler" :isOpened="true" :transitionDuration="150">
            <template #heading>
              <div class="global-h3">
                <span
                  ><span>
                    {{ props.collabInput.useLocationsInVisitPlan ? "Activities" : "Activities and Boundary" }}
                  </span></span
                >
              </div>
            </template>
            <template #details>
              <div class="plan-edit-form__activities-section">
                <!-- Activity snippet -->
                <div class="activity-snippet plan-edit-form__activity-snippet">
                  <div class="activity-snippet__header">
                    <IconEmbedded class="activity-snippet__title-icon" name="zap-solid" />
                    <h5 class="global-h5 activity-snippet__title">Required Locations</h5>
                  </div>

                  <div class="activity-snippet__content">
                    {{ props.collabInput.requiredAttractions }}
                  </div>
                </div>
                <!-- / Activity snippet -->

                <!-- Activity snippet -->
                <div class="activity-snippet plan-edit-form__activity-snippet">
                  <div class="activity-snippet__header">
                    <IconEmbedded class="activity-snippet__title-icon" name="zap_2" />
                    <h5 class="global-h5 activity-snippet__title">Suggested Locations</h5>
                  </div>

                  <div class="activity-snippet__content">
                    {{ props.collabInput.suggestedAttractions }}
                  </div>
                </div>
                <!-- / Activity snippet -->
              </div>

              <!-- Boundary section -->
              <div v-if="!props.collabInput.useLocationsInVisitPlan" class="boundary-section plan-edit-form__boundary-section">
                <div class="boundary-section__header">
                  <h5 class="global-h5 boundary-section__title">Boundary</h5>
                  <div class="boundary-section__description">This is the destination boundary. We recommend verifying any stops outside this boundary with the destination.</div>
                </div>

                <DrawingMap
                  v-if="boundaryData"
                  class="boundary-section__map"
                  :lat="boundaryData.latitude"
                  :lng="boundaryData.longitude"
                  :inputGeoJson="boundaryData.boundaryGeoJson"
                  :circleRadiusInMiles="+boundaryData.withinMiles"
                  :boundarySelectMode="boundaryData.boundarySelectMode"
                  :onlyAllowDrawingMode="true"
                  :showBoundaryAsReadonly="true"
                  :isCreatorFlow="false"
                />
                <!-- Map placeholder -->
                <div v-else class="map-placeholder boundary-section__map-placeholder">
                  <div class="map-placeholder__text">Loading map<EllipsisAnimated /></div>
                  <img class="map-placeholder__bg-image" src="/images/map-placeholder.gif" alt="Map" />
                </div>
                <!-- / Map placeholder -->
              </div>
              <!-- / Boundary section -->
            </template>
          </SrpDetailsSummary>
        </template>
      </div>
    </template>
  </SrpModal>
</template>

<!-- prettier-ignore -->
<script lang="ts">export default { name: "PlanEditModal" };</script>

<script setup lang="ts">
import axios from "axios";
import { inject, ref, onMounted, watch, computed, watchEffect } from "vue";
import isEqual from "lodash-es/isEqual";

// Components
import DrawingMap from "@views/Community/DrawingMap.vue";
import EllipsisAnimated from "@components/ui/EllipsisAnimated.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpDetailsSummary from "@components/ui/SrpDetailsSummary.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import CopyTextNew from "@components/CopyTextNew.vue";
import LocationsList from "./LocationsList.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

// Types
import { CollabCreatorInput, VisitPlanFields, CollabInputSummary } from "@contracts/collab";
import { CommunityBoundaryResponse } from "@contracts/pages";
import { CollabLocationsData } from "@contracts/collabLocations";

// Stores
import { useCollabLocationsStore } from "@stores/collabLocations";
const collabLocationsStore = useCollabLocationsStore();

const props = withDefaults(
  defineProps<{
    showVisitPlanFields: boolean;
    isVisible: boolean;
    creatorInput: CollabCreatorInput | null;
    communityId: string;
    collabInput: CollabInputSummary;
    creatorId: string;
  }>(),
  {
    showVisitPlanFields: false,
    isVisible: true,
    creatorInput: null,
    communityId: "",
    collabInput: null,
    creatorId: "",
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", value: boolean): void;
  (e: "showChat"): void;
  (e: "updatePlan", value: VisitPlanFields): void;
  (e: "updateCollabLocationsData", value: CollabLocationsData): void;
}>();

watch(
  () => props.isVisible,
  value => {
    if (value) {
      errorMessage.value = null;
    }
  }
);

// const screenSize = inject("screenSize") as Ref<ScreenSize>;
const globalRemoteLogger = inject("globalRemoteLogger") as any;

// Load plan text =============================================================
const _visitPlan = ref("");

const collabLocationsData = ref<CollabLocationsData>({ ...collabLocationsStore.collabLocationsData });
const isDirty = computed(() => {
  // NOTE: I don't think this works??
  return !isEqual(collabLocationsData.value, collabLocationsStore.collabLocationsData);
});
onMounted(async () => {
  console.info("Location PlanEditModal Mounted");
});

// Switch to chat =============================================================
function switchToChat() {
  emit("showChat");
}

// Send to destination ========================================================
const errorMessage = ref(null);

// collab locations to save are only those locations that have been "filled out"
const collabLocationsDataToSave = computed<CollabLocationsData>(() => ({
  ...collabLocationsData.value,
  locations: collabLocationsData.value.locations.filter(location => location.title || location.externalId),
}));

const isSendingToDestination = ref<boolean>(false);

async function sendToDestination() {
  isSendingToDestination.value = true;

  if (collabLocationsDataToSave.value.locations.length < props.collabInput.requestedLocationCount) {
    errorMessage.value = `Add ${props.collabInput.requestedLocationCount} locations before sending to the destination.`;
    isSendingToDestination.value = false;
    return;
  }
  // NOTE: Keep this logic in sync with the LocationInputSnippet.vue
  if (collabLocationsDataToSave.value.locations.some(location => (location.title || location.externalId) && !location.latitude)) {
    errorMessage.value = "Locations must have a map position. Use the autocomplete or set a location manually on the map.";
    isSendingToDestination.value = false;
    return;
  }

  globalRemoteLogger.info(`Marking the Visit Plan as "Sent to the Destination". communityId: ${props.communityId}, creatorId: ${props.creatorId}, collabInputId: ${props.collabInput.id}`);

  const { data } = await axios.put<CollabLocationsData>(
    `${import.meta.env.VITE_API_URL}/collabs/${props.communityId}/inputs/${props.collabInput.id}/creator/${props.creatorId}/send-visit-plan-to-destination`
  );
  collabLocationsData.value = data;

  await collabLocationsStore.loadCollabLocationsData(props.collabInput.id, props.creatorId, props.communityId);

  collabLocationsStore.isCollabLocationsDataDirty = false;

  if (props.showVisitPlanFields) {
    const visitPlanFields: VisitPlanFields = {
      visitPlan: _visitPlan.value,
      visitPlanSentDate: new Date().toISOString(),
      visitPlanSignOffDate: props.creatorInput.visitPlanSignOffDate,
    };
    emit("updatePlan", visitPlanFields);
  }
  emit("update:isVisible", false);

  isSendingToDestination.value = false;
}

// Is plan was changed ========================================================
const isPlanWasChanged = computed<boolean>(() => {
  if (props.collabInput.useLocationsInVisitPlan || (!props.showVisitPlanFields && props.collabInput.useLocationsInAdventures)) {
    return isDirty.value;
  } else {
    return (_visitPlan.value || "") !== (props.creatorInput?.visitPlan || "");
  }
});

// Load community boundary data ===============================================
const boundaryData = ref<CommunityBoundaryResponse | null>(null);
const isLoadingBoundary = ref(false);

async function loadCommunityBoundaryData(communityId: string) {
  isLoadingBoundary.value = true;

  const uri = `${import.meta.env.VITE_API_URL}/pages/${communityId}/boundary`;

  try {
    const { data } = await axios.get<CommunityBoundaryResponse>(uri);
    boundaryData.value = data;
  } catch (error) {
    globalRemoteLogger.error(`CommunityBoundaryData is not loaded. communityId: ${communityId}. Error: ${error}`);
  }

  isLoadingBoundary.value = false;
}

watchEffect(async () => {
  if (props.isVisible) {
    await loadCommunityBoundaryData(props.communityId);
  }
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/variables.scss";

// "Send" and "Connect" buttons ===============================================
.send-and-connect-buttons {
  gap: 18px 25px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  &__send-button {
  }

  &__connect-button {
  }
}

// Activity snippet ===========================================================
.activity-snippet {
  &__header {
    margin-bottom: 3px;
    display: flex;
  }

  &__title-icon {
    margin: 0 2px 0 -7px;
    font-size: 26px;
    line-height: 26px;
  }

  &__title {
    content: "";
  }

  &__content {
    white-space: pre-wrap;
  }
}

// Map placeholder ============================================================
.map-placeholder {
  height: 220px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 0;
  overflow: hidden;
  font-weight: bold;
  text-align: center;
  background: white;

  &__text {
    text-align: center;
  }

  &__bg-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: -1;
    opacity: 0.7;
  }
}

// Boundary section ===========================================================
.boundary-section {
  &__header {
    margin-bottom: 10px;
  }

  &__title {
    margin-bottom: 7px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__description {
    content: "";
  }

  &__map {
    height: 450px;
    background: rgba(0, 0, 0, 0.1);

    :deep(.vue-map-container) {
      height: 100% !important;
    }

    :deep(*[data-name="re-draw button"]) {
      display: none;
    }
  }

  &__map-placeholder {
    content: "";
  }
}

// Plan edit form =============================================================
.plan-edit-form {
  color: #5b5b5b;
  font: 14px/19px sans-serif;

  &__title {
    margin-bottom: 15px;
  }

  &__description {
    margin-bottom: 25px;
  }

  &__notify-destination-note {
    margin-bottom: 15px;
  }

  &__plan-textarea {
    margin-bottom: 43px;
  }

  &__divider {
    margin-bottom: 24px;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  }

  &__activities-and-boundary-spoiler {
    content: "";
  }

  &__activities-section {
    padding-top: 28px;
    margin-bottom: 22px;
    display: flex;
    justify-content: space-between;
  }

  &__activity-snippet {
    width: calc(50% - 14px);
    min-width: calc(50% - 14px);
  }

  &__boundary-section {
    content: "";
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .plan-edit-form {
    &__activities-section {
      flex-direction: column;
    }

    &__activity-snippet {
      width: 100%;
      min-width: 100%;
      margin-bottom: 20px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .plan-edit-form {
    &__activities-section {
      flex-direction: column;
    }

    &__activity-snippet {
      width: 100%;
      min-width: 100%;
      margin-bottom: 20px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

// Validation message
.validation-message {
  color: red;
  display: block;
  width: 100%;
  // margin-bottom: 8px;
  font-family: $font-family;
}
</style>
